import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { ordersAPI, Order } from '../../services/api'

export interface OrdersState {
  orders: Order[]
  currentOrder: Order | null
  total: number
  isLoading: boolean
  error: string | null
  filters: {
    status?: string
    priority?: string
    search?: string
  }
  pagination: {
    page: number
    limit: number
  }
}

const initialState: OrdersState = {
  orders: [],
  currentOrder: null,
  total: 0,
  isLoading: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 10,
  },
}

// Async thunks
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (params: {
    skip?: number
    limit?: number
    status?: string
    priority?: string
    search?: string
  } = {}, { rejectWithValue }: any) => {
    try {
      const response = await ordersAPI.getOrders(params)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch orders')
    }
  }
)

export const fetchOrder = createAsyncThunk(
  'orders/fetchOrder',
  async (orderId: number, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.getOrder(orderId)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch order')
    }
  }
)

export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (orderData: Partial<Order>, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.createOrder(orderData)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create order')
    }
  }
)

export const updateOrder = createAsyncThunk(
  'orders/updateOrder',
  async ({ orderId, orderData }: { orderId: number; orderData: Partial<Order> }, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.updateOrder(orderId, orderData)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update order')
    }
  }
)

export const deleteOrder = createAsyncThunk(
  'orders/deleteOrder',
  async (orderId: number, { rejectWithValue }) => {
    try {
      await ordersAPI.deleteOrder(orderId)
      return orderId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete order')
    }
  }
)

export const updateOrderStatus = createAsyncThunk(
  'orders/updateOrderStatus',
  async ({ orderId, status }: { orderId: number; status: string }, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.updateOrderStatus(orderId, status)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update order status')
    }
  }
)

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<OrdersState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {}
    },
    setPagination: (state, action: PayloadAction<Partial<OrdersState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null
    },
    // Real-time updates
    addOrder: (state, action: PayloadAction<Order>) => {
      state.orders.unshift(action.payload)
      state.total += 1
    },
    updateOrderInList: (state, action: PayloadAction<Order>) => {
      const index = state.orders.findIndex(order => order.order_id === action.payload.order_id)
      if (index !== -1) {
        state.orders[index] = action.payload
      }
      if (state.currentOrder?.order_id === action.payload.order_id) {
        state.currentOrder = action.payload
      }
    },
    removeOrderFromList: (state, action: PayloadAction<number>) => {
      state.orders = state.orders.filter(order => order.order_id !== action.payload)
      state.total -= 1
      if (state.currentOrder?.order_id === action.payload) {
        state.currentOrder = null
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchOrders.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.isLoading = false
        state.orders = action.payload.orders
        state.total = action.payload.total
        state.error = null
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        // Ensure orders array is never undefined
        if (!state.orders) {
          state.orders = []
        }
      })
      // Fetch single order
      .addCase(fetchOrder.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchOrder.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentOrder = action.payload
        state.error = null
      })
      .addCase(fetchOrder.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Create order
      .addCase(createOrder.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.isLoading = false
        state.orders.unshift(action.payload)
        state.total += 1
        state.error = null
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Update order
      .addCase(updateOrder.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.isLoading = false
        const index = state.orders.findIndex(order => order.order_id === action.payload.order_id)
        if (index !== -1) {
          state.orders[index] = action.payload
        }
        if (state.currentOrder?.order_id === action.payload.order_id) {
          state.currentOrder = action.payload
        }
        state.error = null
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Delete order
      .addCase(deleteOrder.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.isLoading = false
        state.orders = state.orders.filter(order => order.order_id !== action.payload)
        state.total -= 1
        if (state.currentOrder?.order_id === action.payload) {
          state.currentOrder = null
        }
        state.error = null
      })
      .addCase(deleteOrder.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Update order status
      .addCase(updateOrderStatus.pending, (state) => {
        state.error = null
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        const index = state.orders.findIndex(order => order.order_id === action.payload.order_id)
        if (index !== -1) {
          state.orders[index] = action.payload
        }
        if (state.currentOrder?.order_id === action.payload.order_id) {
          state.currentOrder = action.payload
        }
        state.error = null
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.error = action.payload as string
      })
  },
})

export const {
  setFilters,
  clearFilters,
  setPagination,
  clearError,
  clearCurrentOrder,
  addOrder,
  updateOrderInList,
  removeOrderFromList,
} = ordersSlice.actions

export default ordersSlice.reducer
